import React, { useEffect, useState } from "react";
import { Dialog, DialogContent, DialogTitle } from "@mui/material";
import { Save, Upload, X } from "lucide-react";
import mealsApi from "../../api/meals";
import { uploadApi } from "../../api/upload";
import type { RecipeDocument } from "./MealRecipeViewer";

interface Props {
  mealId: string;
  pageNumber: number;
  currentUrl: string | null;
  onClose: () => void;
  onSaved: (recipe: RecipeDocument) => void;
}

export default function RecipeImageEditor({ mealId, pageNumber, currentUrl, onClose, onSaved }: Props) {
  const [mode, setMode] = useState("upload");
  const [file, setFile] = useState<File | null>(null);
  const [url, setUrl] = useState("");
  const [preview, setPreview] = useState("");
  const [loaded, setLoaded] = useState(false);
  const [error, setError] = useState("");
  const [saving, setSaving] = useState(false);
  useEffect(() => {
    setLoaded(false);
    if (mode === "upload" && file) {
      const objectUrl = URL.createObjectURL(file);
      setPreview(objectUrl);
      return () => URL.revokeObjectURL(objectUrl);
    }
    setPreview(mode === "url" && /^https:\/\//i.test(url.trim()) ? url.trim() : "");
  }, [mode, file, url]);

  const save = async () => {
    if (!loaded || saving) return;
    setSaving(true);
    setError("");
    try {
      const imageUrl = mode === "upload" ? (await uploadApi.uploadRecipeImage(file!)).imageUrl : url.trim();
      const data = await mealsApi.updateRecipeImage(mealId, pageNumber, imageUrl, currentUrl);
      onSaved(data.recipeDocument);
      onClose();
    } catch (err: any) {
      setError(err.response?.status === 413 ? "Image exceeds the server upload limit." : err.response?.data?.message || "Could not save recipe image. Try again.");
    } finally { setSaving(false); }
  };

  return <Dialog open onClose={() => { if (!saving) onClose(); }} maxWidth="sm" fullWidth PaperProps={{ sx: { borderRadius: 2 } }}>
    <DialogTitle className="flex justify-between items-center gap-3">
      <span className="text-base font-semibold">Update Recipe Image - Page {pageNumber}</span>
      <button aria-label="Close recipe image editor" title="Close" disabled={saving} onClick={onClose} className="p-2 disabled:opacity-40"><X size={18} /></button>
    </DialogTitle>
    <DialogContent>
      <div role="tablist" aria-label="Recipe image source" className="flex border-b mb-4 gap-5">
        {[['upload', 'Upload'], ['url', 'Image URL']].map(([value, label]) => <button key={value} role="tab" aria-selected={mode === value} disabled={saving}
          onClick={() => { setMode(value); setError(""); }} className={`py-2 text-sm border-b-2 ${mode === value ? "border-green-700 text-green-800" : "border-transparent text-gray-600"}`}>{label}</button>)}
      </div>
      {mode === "upload" ? <label className="block text-sm font-medium">Recipe image
        <input key="file" aria-label="Recipe image file" type="file" accept="image/png,image/jpeg,image/webp" disabled={saving} className="block w-full mt-2 text-sm"
          onChange={e => {
            const next = e.target.files?.[0];
            setError(""); setFile(null);
            if (!next) return;
            if (!["image/png", "image/jpeg", "image/webp"].includes(next.type) || next.size > 10 * 1024 * 1024) {
              setError("Choose a PNG, JPEG or WebP image up to 10 MB."); return;
            }
            setFile(next);
          }} />
      </label> : <label className="block text-sm font-medium">Image URL
        <input key="url" type="url" value={url} disabled={saving} onChange={e => { setUrl(e.target.value); setError(""); }} placeholder="https://"
          className="block w-full border border-gray-300 rounded p-2 mt-2 font-normal" />
      </label>}
      {preview && <div className="mt-4 border border-gray-200 bg-gray-100 h-72 overflow-auto">
        <img key={preview} src={preview} alt="Recipe image preview" className="w-full h-auto" onLoad={() => setLoaded(true)}
          onError={() => { setLoaded(false); setError("Could not load this image."); }} />
      </div>}
      {error && <p role="alert" className="mt-3 text-sm text-red-700">{error}</p>}
      <div className="flex justify-end gap-3 mt-5">
        <button disabled={saving} onClick={onClose} className="text-sm px-3 py-2">Cancel</button>
        <button disabled={!loaded || saving} onClick={save} className="flex items-center gap-2 bg-green-700 text-white rounded px-4 py-2 text-sm disabled:opacity-40">
          {saving ? <Upload size={16} /> : <Save size={16} />} {saving ? "Saving..." : "Save"}
        </button>
      </div>
    </DialogContent>
  </Dialog>;
}
